import React, { useState } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, QrCodeIcon, ScanLineIcon, XCircleIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Field, Input } from '../ui/Field';
import { useCampaGift } from '../../contexts/CampaGiftContext';
import type { CampaGift } from '../../types/campagift';
import { STATUS_LABEL, bpsLabel, commissionFor, koboToNaira, nairaToKobo, statusTone } from '../../utils/campagift';

/**
 * The merchant's redemption desk. Nothing here decides eligibility: it calls the
 * engine and renders every rule it checked, so a refusal always explains itself.
 */
export function RedemptionDesk() {
  const {
    lookupByCode,
    eligibility,
    redeem,
    typeOf,
    remaining,
    status,
    merchantId,
    merchantOf,
    codeLockedUntil
  } = useCampaGift();
  const [code, setCode] = useState('');
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [gift, setGift] = useState<CampaGift | null>(null);
  const [amount, setAmount] = useState('');
  const [result, setResult] = useState<{
    kind: 'confirmed';
    reference: string;
    amountKobo: number;
    commissionKobo: number;
    netKobo: number;
  } | {
    kind: 'refused';
    error: string;
  } | null>(null);
  const merchant = merchantOf(merchantId);
  const type = gift ? typeOf(gift) : null;
  const check = gift ? eligibility(gift.id, merchantId) : null;
  const requestedKobo = amount ? nairaToKobo(amount) : null;
  const requestCheck = gift && requestedKobo !== null ? eligibility(gift.id, merchantId, requestedKobo) : null;
  function onLookup() {
    setResult(null);
    setGift(null);
    const found = lookupByCode(code);
    if (!found.ok) {
      setLookupError(found.error);
      return;
    }
    setLookupError(null);
    setGift(found.gift);
    setAmount('');
  }
  function onRedeem() {
    if (!gift || requestedKobo === null || !merchant || !type) return;
    const outcome = redeem({
      giftId: gift.id,
      merchantId,
      amountKobo: requestedKobo,
      actor: `${merchant.name} · till`,
      /* One key per attempt: a repeated submit can never spend twice. */
      idempotencyKey: `idem-${gift.id}-${requestedKobo}-${Math.floor(Date.now() / 1000)}`
    });
    if (!outcome.ok) {
      setResult({
        kind: 'refused',
        error: outcome.error
      });
      return;
    }
    setResult({
      kind: 'confirmed',
      reference: outcome.redemption.reference,
      amountKobo: outcome.redemption.amountKobo,
      commissionKobo: outcome.redemption.commissionKobo,
      netKobo: outcome.redemption.netReceivableKobo
    });
    setAmount('');
  }
  const locked = codeLockedUntil !== null && Date.now() < codeLockedUntil;
  return <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]" data-id="element-697" data-mp="2vzd8xeou9v09">
      <div className="space-y-6" data-id="element-698" data-mp="2w16gqxb6ul9u">
        <Panel title="Accept a CampaGift" description="Scan the customer's QR code, or type the code they show you." data-id="element-699" data-mp="300yox8wo6peo">
          <div className="flex flex-wrap items-end gap-3" data-id="element-700" data-mp="2si7kr1st0f9o">
            <div className="min-w-[220px] flex-1" data-id="element-701" data-mp="2nqzak3jngpvg">
              <Field label="Gift code" htmlFor="desk-code" hint="Format: XXXX-XXXX-XXXX" data-id="element-702" data-mp="2npeickkt5j0r">
                <Input id="desk-code" value={code} onChange={event => setCode(event.target.value.toUpperCase())} placeholder="K7QM-3JHR-9TWX" className="cz-num tracking-wide" autoComplete="off" data-id="element-703" data-mp="2is5xgc89z0vt" />
              </Field>
            </div>
            <Button variant="primary" onClick={onLookup} disabled={code.trim().length < 6 || locked} icon={<ScanLineIcon aria-hidden className="h-4 w-4" data-id="element-705" data-mp="2xg98i016d2rh" />} data-id="element-704" data-mp="32f3x5iltplji">
              Look up gift
            </Button>
            <Button variant="secondary" icon={<QrCodeIcon aria-hidden className="h-4 w-4" data-id="element-707" data-mp="1lzsn7q2xvrwp" />} disabled data-id="element-706" data-mp="1m1dhdelcd8u1">
              Scan QR
            </Button>
          </div>

          {lookupError && <p className="mt-3 flex items-start gap-2 rounded-md border border-danger-100 bg-danger-50 px-3 py-2.5 text-body-sm text-danger-700" data-id="element-708" data-mp="3epjv4i3s7jac">
              <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-709" data-mp="30vk9hk1r4y4h" />
              {lookupError}
            </p>}

          <p className="mt-3 text-caption text-ink-muted" data-id="element-710" data-mp="3sob6hwr9equs">
            Codes are verified against a stored hash on the server. Wrong entries are counted, and
            five failures locks code entry for a minute and raises a risk flag.
          </p>
        </Panel>

        {gift && type && check && <Panel title={gift.publicReference} description={type.name} action={<Badge tone={statusTone(status(gift))} plain={status(gift) === 'available'} data-id="element-712" data-mp="2874ugximeft5">
                {STATUS_LABEL[status(gift)]}
              </Badge>} data-id="element-711" data-mp="3olwotfzbtx53">
            <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-713" data-mp="24pqy361eqixp">
              <div data-id="element-714" data-mp="20pq7yo4y4jat">
                <p className="text-body-sm text-ink-muted" data-id="element-715" data-mp="20o5dszmjn2dh">Remaining value</p>
                <p className="cz-num mt-1 text-h1 font-semibold text-ink" data-id="element-716" data-mp="2ephqn26eqnv5">
                  {koboToNaira(remaining(gift))}
                </p>
              </div>
              <div className="text-right" data-id="element-717" data-mp="29o25la48chcr">
                <p className="text-body-sm text-ink-muted" data-id="element-718" data-mp="1lziz904qqenb">Redeemable here now</p>
                <p className="cz-num mt-1 text-h3 font-semibold text-ink" data-id="element-719" data-mp="1h1hbw30a7t61">
                  {koboToNaira(check.maxRedeemableKobo)}
                </p>
              </div>
            </div>

            <ul className="mt-4 divide-y divide-line border-y border-line" data-id="element-720" data-mp="1qzdl2p9nse1i">
              {check.checks.map(item => <li key={item.rule} className="flex items-start gap-2.5 py-2.5" data-id="element-721" data-mp="139182zzk3r42">
                  {item.passed ? <CheckCircle2Icon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ok-700" data-id="element-722" data-mp="y9wyi88pzrd7" /> : <XCircleIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-danger-700" data-id="element-723" data-mp="33b0q4uhvzceo" />}
                  <div className="min-w-0" data-id="element-724" data-mp="383aomyx2hksk">
                    <p className="text-body-sm font-medium text-ink" data-id="element-725" data-mp="3c4cnvfqzhwuj">{item.rule}</p>
                    <p className="text-caption text-ink-muted" data-id="element-726" data-mp="3c670m6s3qinj">{item.detail}</p>
                  </div>
                </li>)}
            </ul>

            {check.eligible ? <div className="mt-4" data-id="element-727" data-mp="2kfanr661ke09">
                <div className="flex flex-wrap items-end gap-3" data-id="element-728" data-mp="2oea19f5u9j3b">
                  <div className="min-w-[200px] flex-1" data-id="element-729" data-mp="2ogw4j2lp5cfq">
                    <Field label="Redemption amount" htmlFor="desk-amount" hint={type.partialRedemption ? `From ${koboToNaira(type.minRedemptionKobo)} up to ${koboToNaira(check.maxRedeemableKobo)}` : `Must be the full ${koboToNaira(remaining(gift))}`} data-id="element-730" data-mp="2texn7mf7n9qo">
                      <Input id="desk-amount" inputMode="decimal" value={amount} onChange={event => setAmount(event.target.value)} placeholder="5000" data-id="element-731" data-mp="dukg7fgstdd1" />
                    </Field>
                  </div>
                  <Button variant="primary" onClick={onRedeem} disabled={!requestCheck?.eligible} data-id="element-732" data-mp="3r74cegj0viez">
                    Confirm redemption
                  </Button>
                </div>

                {requestedKobo !== null && requestCheck && !requestCheck.eligible && <p className="mt-2 flex items-start gap-2 text-body-sm text-danger-700" data-id="element-733" data-mp="1kelhw2ioy3dm">
                    <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-734" data-mp="1gd88xjkaxk2h" />
                    {requestCheck.checks.find(c => !c.passed)?.detail}
                  </p>}

                {requestedKobo !== null && requestCheck?.eligible && merchant && <dl className="mt-3 divide-y divide-line rounded-card border border-line text-body-sm" data-id="element-735" data-mp="1py12y87ytw55">
                    {(() => {
              const {
                commissionKobo,
                netKobo,
                bps
              } = commissionFor(requestedKobo, type, merchant);
              return <>
                          <div className="flex justify-between px-4 py-2.5" data-id="element-736" data-mp="1719piff3ub90">
                            <dt className="text-ink-muted" data-id="element-737" data-mp="1bzb86z8mc8jy">Gross redemption</dt>
                            <dd className="cz-num text-ink" data-id="element-738" data-mp="31d6fe3xwf91y">{koboToNaira(requestedKobo)}</dd>
                          </div>
                          <div className="flex justify-between px-4 py-2.5" data-id="element-739" data-mp="36c10xdn93bpr">
                            <dt className="text-ink-muted" data-id="element-740" data-mp="3ghj2r6jaazq2">
                              Campuzon commission ({bpsLabel(bps)})
                            </dt>
                            <dd className="cz-num text-ink" data-id="element-741" data-mp="3p8rqln7pqwaa">−{koboToNaira(commissionKobo).replace('₦', '₦')}</dd>
                          </div>
                          <div className="flex justify-between bg-canvas px-4 py-2.5" data-id="element-742" data-mp="3kh9o2uq1knn7">
                            <dt className="font-semibold text-ink" data-id="element-743" data-mp="30s249kduk4v">Your receivable</dt>
                            <dd className="cz-num font-semibold text-ink" data-id="element-744" data-mp="3u8e3twnjkcj3">
                              {koboToNaira(netKobo)}
                            </dd>
                          </div>
                        </>;
            })()}
                  </dl>}
              </div> : <p className="mt-4 flex items-start gap-2 rounded-card border border-danger-100 bg-danger-50 px-4 py-3 text-body-sm text-ink" data-id="element-745" data-mp="2du4n9opjjs2t">
                <XCircleIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-danger-700" data-id="element-746" data-mp="v9ffic4w6uzv" />
                <span data-id="element-747" data-mp="r83n5yumvvnz">
                  This gift cannot be redeemed here. The failed rule is listed above — explain it to
                  the customer rather than retrying.
                </span>
              </p>}
          </Panel>}

        {result?.kind === 'confirmed' && <Panel title="Redemption confirmed" data-id="element-748" data-mp="1exer1p7a666c">
            <p className="flex items-start gap-2 text-body text-ink" data-id="element-749" data-mp="1ewd68t0gq5bf">
              <CheckCircle2Icon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ok-700" data-id="element-750" data-mp="19ybk1z7qppvq" />
              <span data-id="element-751" data-mp="157blyqmvx1rn">
                {koboToNaira(result.amountKobo)} redeemed. Receipt{' '}
                <span className="cz-num font-semibold" data-id="element-752" data-mp="109a6efoo3kkx">{result.reference}</span>.
              </span>
            </p>
            <dl className="mt-3 space-y-1.5 text-body-sm" data-id="element-753" data-mp="1xofqsfg865qy">
              <div className="flex justify-between" data-id="element-754" data-mp="1xpz1zrn0xqlv">
                <dt className="text-ink-muted" data-id="element-755" data-mp="1swnwbtty7fvj">Commission</dt>
                <dd className="cz-num text-ink" data-id="element-756" data-mp="208u4w79kunf0">{koboToNaira(result.commissionKobo)}</dd>
              </div>
              <div className="flex justify-between" data-id="element-757" data-mp="2575f4j3sazxr">
                <dt className="text-ink-muted" data-id="element-758" data-mp="259rkqd7475dc">Added to your receivable</dt>
                <dd className="cz-num font-semibold text-ink" data-id="element-759" data-mp="2a5yqa4vlmeur">{koboToNaira(result.netKobo)}</dd>
              </div>
            </dl>
          </Panel>}

        {result?.kind === 'refused' && <Panel title="Redemption refused" data-id="element-760" data-mp="2j6wzr2ot3ekq">
            <p className="flex items-start gap-2 text-body text-ink" data-id="element-761" data-mp="2o6jbt8sweory">
              <XCircleIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-danger-700" data-id="element-762" data-mp="2o4q1njj2tmf7" />
              {result.error}
            </p>
          </Panel>}
      </div>

      <div className="space-y-6" data-id="element-763" data-mp="2xv1a9jdpgjsf">
        <Panel title="Try it with a seeded gift" data-id="element-764" data-mp="2xy6ykweifhn3">
          <p className="text-body-sm text-ink-muted" data-id="element-765" data-mp="32wgvbbfvuqat">
            Development codes for the demonstration state:
          </p>
          <ul className="mt-2 space-y-2" data-id="element-766" data-mp="36xaig2li412n">
            {[{
            code: 'K7QM-3JHR-9TWX',
            note: '₦10,000 General — accepted here'
          }, {
            code: 'B4LP-8XND-2VKS',
            note: '₦15,000 Books, partly used — accepted here'
          }, {
            code: 'R9TC-5MFJ-7HQZ',
            note: '₦5,000 campaign gift — ₦2,500 daily cap'
          }, {
            code: 'M8HV-2QTB-5JLC',
            note: 'Fully redeemed — will be refused'
          }].map(item => <li key={item.code} data-id="element-767" data-mp="1qiifk8451p76">
                <button type="button" onClick={() => setCode(item.code)} className="cz-num min-h-touch w-full rounded-md border border-line px-3 py-2 text-left text-body-sm text-ink transition-colors duration-150 ease-out hover:border-line-strong" data-id="element-768" data-mp="1qhh2jy3kasmt">
                  {item.code}
                  <span className="mt-0.5 block text-caption text-ink-muted" data-id="element-769" data-mp="55fn2c6i1lsu">{item.note}</span>
                </button>
              </li>)}
          </ul>
        </Panel>

        <Panel title="What you may not do" data-id="element-770" data-mp="15x43ly8nksn">
          <ul className="space-y-2 text-body-sm text-ink-muted" data-id="element-771" data-mp="3n51fi6kzfk52">
            <li data-id="element-772" data-mp="3n7npsdz9cdqz">· Never ask a customer for their Campuzon password or PIN.</li>
            <li data-id="element-773" data-mp="3r63kl41kyqey">· Never redeem more than the customer is purchasing.</li>
            <li data-id="element-774" data-mp="3deoxal0n4323">· Never redeem a category-bound gift against ineligible goods.</li>
            <li data-id="element-775" data-mp="3idrrfaf30ova">· Never reverse your own redemption — raise it with finance.</li>
          </ul>
        </Panel>
      </div>
    </div>;
}